<template>
  <div class="info-container">
    <update-va v-if="getterPopupName === 'UpdateVA'" />
    <update-remitters v-else-if="getterPopupName === 'Whitelist'" />
    <bank-transfer v-else-if="getterPopupName === 'BankTransfer' && isVaSelf"
      @updateSubVaBalance="updateBalance"
      :subVaBalance="subVaBalance"
      :manageAccess="manageAccess"
      :vaId="vaId"
    />

    <div class="details-panel">
      <h6>Virtual Account Details</h6>
      <button v-if="vaDetails && manageAccess.manageVa" @click="openPopup('UpdateVA')"
        class="outline-btn pos-abs">
        Update
      </button>

      <div class="row" v-for="(va, vaIndex) in vaDetails" :key="vaIndex">
        <b>{{va.title}}</b>
        <span>:</span>
        <div v-if="typeof va.value === 'object'">
          <p v-for="(data, dataIndex) in va.value" :key="dataIndex">
            {{data.head}}
            <i class="fa fa-info-circle" v-if="data.reason">
              <md-tooltip md-direction="top">{{data.reason}}</md-tooltip>
            </i>
            <i v-else>{{data.content}}</i>
          </p>
        </div>
        <p v-else-if="va.func" @click="va.func">
          <u>{{va.value}}</u>
        </p>
        <p v-else>{{va.value}}</p>

        <span v-if="va.title === 'Whitelisted Remitters' && manageAccess.manageVa"
              @click="openPopup('Whitelist')">
          <i v-if="va.value" class="fa fa-pencil-alt" />
          <u v-else>Add Remitters</u>
        </span>
        <button v-else-if="va.options" @click="openPopup(va.options.popup)">
          {{va.options.text}}
        </button>
      </div>

      <p class="centered" v-if="!vaDetails">Loading...</p>

      <div class="actions" v-if="actions.length">
        <button v-for="(action, actionIndex) in actions"
          :key="actionIndex"
          @click="openPopup(action.popup)"
          class="primary-btn"
        >
          <i class="fa" :class="action.icon"></i>
          {{action.text}}
        </button>
      </div>
    </div>

    <button v-if="isDesktop()" class="primary-btn" @click="goBack">
      <i class="fa fa-angle-left" />Back
    </button>
  </div>
</template>

<script>
import UpdateVa from '@/components/VirtualAccounts/Form'
import UpdateRemitters from '@/components/VirtualAccounts/WhitelistRemitters/Update'
import BankTransfer from '@/components/AccountSummary/BankTransfer'

import { goBack, openPopup, amountFormat } from '@/utils/helpers'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'VADetails',
  components: { UpdateVa, UpdateRemitters, BankTransfer },
  data () {
    return {
      vaDetails: null,
      manageAccess: {
        manageVa: true,
        vaBalance: false
      },
      isVaSelf: false,
      subVaBalance: 0,
      vaId: null,
      actions: []
    }
  },
  beforeDestroy () {
    this.SET_SECTION_DETAILS(null)
    this.SET_VA_CHILDS(false)
  },
  computed: {
    ...mapGetters(['getterSectionDetails', 'getterPopupName'])
  },
  watch: {
    getterSectionDetails (newVal) {
      if (newVal) {
        this.populateData(newVal)
      }
    }
  },
  methods: {
    goBack,
    openPopup,
    amountFormat,
    ...mapMutations(['SET_SECTION_DETAILS', 'SET_VA_CHILDS', 'CHANGE_VA_BALANCE']),
    populateData (data) {
      const privileges = data.privileges
      if (privileges) {
        this.manageAccess = {
          manageVa: privileges.manage_virtual_accounts,
          vaBalance: privileges.va_balance
        }
      }

      const va = data.va
      this.isVaSelf = va.settle_to === 'SELF'
      this.vaDetails = [
        this.getKeyVal('ID', va.id),
        this.getKeyVal('Account Details', this.getBankData(va)),
        this.getKeyVal('Created At', va.created_at),
        this.getKeyVal('Reference Number', va.reference_number),
        this.getKeyVal('UDF1', va.udf1),
        this.getKeyVal('UDF2', va.udf2),
        this.getKeyVal('UDF3', va.udf3),
        this.getKeyVal('Status', va.status),
        this.getKeyVal('Whitelisted Remitters', this.getWhitelisters(va))
      ]

      if (data.self_va_enabled) {
        this.showWalletDetails(va, privileges)
      }
    },
    insertInPos () {
      let posIndex = 2

      return {
        insert: (title, value, options) => {
          this.vaDetails.splice(posIndex++, 0, this.getKeyVal(title, value, options))
        },
        insertHyperlink: (title, value, func) => {
          this.vaDetails.splice(posIndex++, 0, {
            title,
            value,
            func
          })
        }
      }
    },
    showWalletDetails (va, privileges) {
      this.actions = []
      const insertInPos = this.insertInPos()
      const insert = insertInPos.insert
      insert('Settle To', va.settle_to)

      if (this.isVaSelf) {
        let vaBalance = '-'
        if (!privileges.va_balance) {
          vaBalance = 'Please contact Admin for VA Balance Access'
        } else if (va.account_balance !== '-') {
          vaBalance = this.amountFormat(va.account_balance)
          this.subVaBalance = va.account_balance
        }

        if (privileges.manage_va_transfer) {
          this.actions.push({
            text: 'Bank Transfer',
            popup: 'BankTransfer',
            icon: 'fa-exchange-alt'
          })
        }

        insert('Balance', vaBalance)
        this.vaId = va.id
      }

      insert('Parent Type', va.parent_type)
      insert('Level', va.level)
      if (va.va_lien_amount) {
        insert('VA Lien Amount', this.amountFormat(va.va_lien_amount))
      }

      if (va.parent_type === 'VIRTUAL_ACCOUNT') {
        insertInPos.insertHyperlink('Parent ID', va.parent_id, () => {
          this.SET_SECTION_DETAILS(null)
          this.$router.push({name: 'VATransactions', params: {id: va.parent_id}})
        })
      }

      const upiDetails = va.upi_details
      if (upiDetails) {
        insert('UPI ID', upiDetails.upi_id)
        insert('UPI Name', upiDetails.name)
        insert('UPI Status', upiDetails.status)
        insert('UPI Channel', upiDetails.channel)
      }

      if (this.isVaSelf || va.parent_type === 'VIRTUAL_ACCOUNT') {
        this.SET_VA_CHILDS(true)
      }
    },
    getKeyVal (title, value, options) {
      return {
        title, value, options
      }
    },
    getBankData (va) {
      return va.details.map(entry => {
        return {
          head: entry.account_number + ' / ' + entry.account_ifsc,
          content: '( ' + entry.payment_modes.join(' / ') + ' )',
          reason: entry.reason ? 'Status: DISABLED, please add whitelisted remitter accounts to enable Paytm Virtual accounts' : null
        }
      })
    },
    getWhitelisters (va) {
      let whitelisters = va.whitelisted_remitters || []
      if (whitelisters.length) {
        return whitelisters.map(entry => {
          return {
            head: `${entry.number} / ${entry.ifsc}`
          }
        })
      }
      return this.manageAccess.manageVa ? '' : '-'
    },
    updateBalance (options, transferInitiated) {
      if (transferInitiated) {
        if (this.$route.name === 'VATransactions') {
          this.CHANGE_VA_BALANCE()
        } else {
          this.$router.push({name: 'VATransactions', params: {id: this.vaId}})
        }
      } else {
        const pos = this.vaDetails.findIndex(e => e.title === 'Balance')

        let newVal = {
          ...this.vaDetails[pos],
          value: options.access ? this.amountFormat(options.balance) : 'Please contact Admin for VA Balance Access'
        }
        this.$set(this.vaDetails, pos, newVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/detailsPanel.scss';

  .details-panel {
    .pos-abs {
      position: absolute;
      top: 20px;
      right: 20px;
    }

    .row {
      > div {
        margin-left: 20px;

        p {
          margin: 5px 0;
        }
      }
    }

    .actions {
      @include custom-flex(flex-end);
      margin-top: 20px;

      button {
        i {
          margin-right: 10px;
        }
      }
    }
  }

  .desktop {
    .info-container {
      .details-panel {
        b {
          min-width: 180px !important;
        }

        .actions {
          position: absolute;
          bottom: 25px;
          right: 20px;
        }
      }
    }
  }

  .mobile, .tablet {
    .details-panel {
      .row {
        b {
          min-width: 140px !important;
        }
      }
    }
  }

  .mobile {
    .row {
      > div {
        p {
          font-size: 13px;

          i:not(.fa-info-circle) {
            display: block;
          }
        }
      }
    }
  }
</style>
